import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Modal from './Modal'
import useNetwork from '../models/useNetwork'
import useNode from '../models/useNode'
import useNodeAttach, { type AttachRequest } from '../models/useNodeAttach'
import { useOrgRole } from '../hooks/useOrgRole'
import { getUserId } from '../lib/workos'
import { REGEX_VALID_DOMAIN } from '../constants'
import { toolRefusalMessage } from '../utilities/routerWrites'
import {
  ATTACH_FORBIDDEN,
  DETACH_FORBIDDEN,
  FIRST_ATTACH_LINE,
  KEPT_PRIVATE_LINE,
  attachBlock,
  attachSharingLine,
  candidatesLine,
  detachLines,
  detachSharingLine,
  keptPrivateNodesLine,
  numberedLine,
  numberedNodesLine,
  type AttachPreview,
  type DetachResult,
} from '../utilities/nodeAttach'
import { nodeNameIn } from '../utilities/nodeNetworks'

const ATTACH_REFUSED = 'Could not attach the node.'
const DETACH_REFUSED = 'Could not detach the node.'

/**
 * The one dialog for attach and detach (app#498). Every surface opens it through `useNodeAttach`,
 * so a later drop on the graph, the list or the map opens this too, and a drop never attaches by
 * itself.
 */
export default function NodeAttachDialog() {
  const request = useNodeAttach(state => state.request)
  const close = useNodeAttach(state => state.close)
  const busy = useNodeAttach(state => state.busy)
  if (!request) return null
  return (
    <Modal open onClose={() => !busy && close()} maxWidth="xs" fullWidth>
      {request.mode === 'numbered' ? (
        <Numbered names={request.names} keptPrivate={request.keptPrivate} />
      ) : request.mode === 'detach' ? (
        <Detach nodeId={request.nodeId} networkId={request.networkId} />
      ) : (
        <Attach request={request} />
      )}
    </Modal>
  )
}

function Numbered({ names, keptPrivate }: Readonly<{ names: string[]; keptPrivate: string[] }>) {
  const close = useNodeAttach(state => state.close)
  return (
    <>
      <DialogTitle variant="subtitle1">Network deleted</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {!!names.length && <Typography variant="body2">{numberedNodesLine(names)}</Typography>}
          {!!keptPrivate.length && <Typography variant="body2">{keptPrivateNodesLine(keptPrivate)}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button autoFocus variant="contained" onClick={close}>
          Close
        </Button>
      </DialogActions>
    </>
  )
}

function Detach({ nodeId, networkId }: Readonly<{ nodeId: string; networkId: string }>) {
  const close = useNodeAttach(state => state.close)
  const detach = useNodeAttach(state => state.detach)
  const busy = useNodeAttach(state => state.busy)
  const node = useNode(state => state.nodes.find(n => n.id === nodeId))
  const network = useNetwork(state => state.networks.find(n => n.id === networkId))
  // The node's controllers: its owner, or an Owner or Admin of the node's org.
  const { controls } = useOrgRole(node ? (node.ownerOrgId ?? null) : undefined)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<DetachResult | null>(null)
  // Removed by another session while the dialog is open.
  if (!node || !network)
    return (
      <DialogContent>
        <Typography variant="body2">This node or network is no longer here.</Typography>
      </DialogContent>
    )

  // ⚠ Reversible, so one confirmation and no typing (app#498).
  const onDetach = async () => {
    setError(null)
    try {
      const result = await detach(nodeId, networkId)
      if (result.numbered || result.keptPrivate) setDone(result)
      else close()
    } catch (e) {
      console.error('node_detach failed:', e)
      setError(toolRefusalMessage(e, DETACH_FORBIDDEN, DETACH_REFUSED))
    }
  }

  // The router numbered the name, or set the node to Private because the reader does not control
  // it (router#2200): this is the one place that says so.
  if (done)
    return (
      <>
        <DialogTitle variant="subtitle1">Detached from {network.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {done.numbered && <Typography variant="body2">{numberedLine(done.numbered)}</Typography>}
            {done.keptPrivate && <Typography variant="body2">{KEPT_PRIVATE_LINE}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button autoFocus variant="contained" onClick={close}>
            Close
          </Button>
        </DialogActions>
      </>
    )

  return (
    <>
      <DialogTitle variant="subtitle1">
        Detach {nodeNameIn(node, networkId)} from {network.name}?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {detachLines(node.networkIds.length, network.name).map(line => (
            <Typography key={line} variant="body2">
              {line}
            </Typography>
          ))}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {detachSharingLine(controls(node.ownerUserId))}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" sx={{ color: 'GrayText' }} disabled={busy} onClick={close}>
          Cancel
        </Button>
        <Button autoFocus variant="contained" disabled={busy} onClick={() => void onDetach()}>
          Detach
        </Button>
      </DialogActions>
    </>
  )
}

function Attach({ request }: Readonly<{ request: Extract<AttachRequest, { mode: 'attach' }> }>) {
  const close = useNodeAttach(state => state.close)
  const attach = useNodeAttach(state => state.attach)
  const runPreview = useNodeAttach(state => state.preview)
  const busy = useNodeAttach(state => state.busy)
  const nodes = useNode(state => state.nodes)
  const networks = useNetwork(state => state.networks)
  const { role, controls } = useOrgRole()
  const userId = getUserId()

  const [nodeId, setNodeId] = useState(request.nodeId ?? '')
  const [networkId, setNetworkId] = useState(request.networkId ?? '')
  const node = nodes.find(n => n.id === nodeId)
  const network = networks.find(n => n.id === networkId)
  const block = node && network ? attachBlock(node, network, role, userId) : null

  const [preview, setPreview] = useState<AttachPreview | null>(null)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  // The router's preview. ⚠ A new pick starts again, and an old answer must not land on it.
  useEffect(() => {
    setPreview(null)
    setError(null)
    if (!nodeId || !networkId || !node || block) return
    let live = true
    runPreview(nodeId, networkId, node.name)
      .then(answer => {
        if (!live) return
        setPreview(answer)
        setName(answer.name)
      })
      .catch((e: unknown) => {
        if (!live) return
        console.error('node_attach dry_run failed:', e)
        setError(toolRefusalMessage(e, ATTACH_FORBIDDEN, ATTACH_REFUSED))
      })
    return () => {
      live = false
    }
    // ⚠ Ids only: `node` and `block` are new objects after each store change, and a node that goes
    // offline must not ask the router again.
  }, [nodeId, networkId, runPreview, !!node, !!block])

  const nodeChoices = nodes
    .filter(n => n.id === nodeId || (controls(n.ownerUserId) && !(networkId && n.networkIds.includes(networkId))))
    .sort((a, b) => a.name.localeCompare(b.name))
  // ⚠ The pick stays in the list: the node read after an attach lands before the dialog closes.
  const networkChoices = networks
    .filter(n => !!n.id && (n.id === networkId || !node?.networkIds.includes(n.id)))
    .map(n => ({ network: n, why: node ? attachBlock(node, n, role, userId) : null }))
    .sort((a, b) => a.network.name.localeCompare(b.network.name))

  // ⚠ The router decides, and the dialog shows its refusal: an isolated network has no force action
  // (router#2178 retires them).
  const refused = preview?.refusal
  const waiting = !!node && !!network && !block && !preview && !error
  // What the dialog promises holds only for an attach that can go ahead.
  const open = !!node && !!network && !block && !!preview && !refused && !preview.alreadyMember
  // ⚠ A name is checked only when the user changed it: a node keeps its name, whatever it is.
  const nameChanged = !!preview && name !== preview.name
  const ready = open && (!nameChanged || REGEX_VALID_DOMAIN.test(name))

  const onAttach = async () => {
    if (!node || !network) return
    setError(null)
    try {
      await attach(nodeId, networkId, { name: nameChanged ? name : undefined })
      close()
    } catch (e) {
      console.error('node_attach failed:', e)
      setError(toolRefusalMessage(e, ATTACH_FORBIDDEN, ATTACH_REFUSED))
    }
  }

  return (
    <>
      <DialogTitle variant="subtitle1">Attach {node ? node.name : 'a node'}{network ? ` to ${network.name}` : ' to a network'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ paddingTop: 1 }}>
          {!request.nodeId && (
            <TextField select size="small" label="Node" value={nodeId} onChange={e => setNodeId(e.target.value)}>
              {nodeChoices.map(n => (
                <MenuItem key={n.id} value={n.id}>
                  {n.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!request.nodeId && !nodeChoices.length && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You have no other node to attach to this network.
            </Typography>
          )}
          {!request.networkId && (
            <TextField select size="small" label="Network" value={networkId} onChange={e => setNetworkId(e.target.value)}>
              {networkChoices.map(({ network: n, why }) => (
                <MenuItem key={n.id} value={n.id} disabled={!!why} sx={{ display: 'block', whiteSpace: 'normal' }}>
                  {n.name}
                  {why && (
                    <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                      {why}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!request.networkId && !networkChoices.length && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              This node is in every network of this organization.
            </Typography>
          )}
          {block && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {block}
            </Typography>
          )}
          {waiting && <CircularProgress size={20} aria-label="Asking the router" />}
          {open && !node?.networkIds.length && <Typography variant="body2">{FIRST_ATTACH_LINE}</Typography>}
          {/* ⚠ The router says when this attach opens the node to the org (`opens_to_org`): then
              the dialog says so in plain words, not as a general caption. */}
          {open && preview && (
            <Typography
              variant={preview.opensToOrg ? 'body2' : 'caption'}
              sx={preview.opensToOrg ? undefined : { color: 'text.secondary' }}
            >
              {attachSharingLine(preview)}
            </Typography>
          )}
          {open && node && network && preview && preview.name !== node.name && (
            <>
              <Typography variant="body2">
                This node will be{' '}
                <Typography variant="code" component="span">
                  {name || preview.name}
                </Typography>{' '}
                on {network.name}.
              </Typography>
              {/* A name is a DNS label: the same filter as a rename (`NameForm`). */}
              <TextField
                size="small"
                label="Name on this network"
                value={name}
                onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              />
            </>
          )}
          {open && preview?.ambiguous.map(entry => (
            <Typography key={entry.name} variant="body2" component="div">
              <Typography variant="code" component="span">
                {entry.name}
              </Typography>{' '}
              will no longer resolve on this node.
              {!!entry.candidates.length && (
                <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                  {candidatesLine(entry.candidates)}
                </Typography>
              )}
            </Typography>
          ))}
          {preview?.alreadyMember && network && <Typography variant="body2">This node is already in {network.name}.</Typography>}
          {/* ⚠ A dry run gives `forbidden` as a value, and that text names the tool. */}
          {refused && <Alert severity="error">{toolRefusalMessage(refused, ATTACH_FORBIDDEN, ATTACH_REFUSED)}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" sx={{ color: 'GrayText' }} disabled={busy} onClick={close}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!ready || busy} onClick={() => void onAttach()}>
          Attach
        </Button>
      </DialogActions>
    </>
  )
}
